import React from 'react';
import {
  Box,
  Card,
  Stack,
  Typography,
  Grid,
  Button,
  IconButton,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  TextField,
  Divider,
} from '@mui/material';
import { AddCircleOutline } from '@mui/icons-material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SearchableDropdown from '../Common/SearchableDropdown/SearchableDropdown.tsx';
import CommonBadges from '../../Shared/Badges/index.jsx';
import { getOperatorOptions, valueTypeOptions } from '../queryOperators.ts';

// Type definitions
export type Operator =
  | 'equals'
  | 'not_equals'
  | 'contains'
  | 'starts_with'
  | 'ends_with'
  | 'gt'
  | 'gte'
  | 'lt'
  | 'lte'
  | 'is_empty'
  | 'is_not_empty';

export type ValueType = 'string' | 'number' | 'boolean' | 'date';
export type LogicalOperator = 'AND' | 'OR';

export interface ConditionRule {
  id: string;
  left: string;
  operator: Operator | { label: string; value: Operator };
  valueType: ValueType;
  caseInsensitive?: boolean;
  trim?: boolean;
  entity?: { _id: string; name: string };
  selectedEntityFormField?: { name: string; label: string };
  entityFormFields?: any[];
  table?: { label: string; value: string } | any;
  custom_value?: string;
  campaign?: { proposal_id: string; name: string };
  webForm?: { leads_form_id: number | string; leads_form_name: string } | null;
  matched_with?: { name: string }; // <-- Updated property
  param?: any; // <-- Also add this if used elsewhere
}

export interface LogicalGroup {
  id: string;
  operator: LogicalOperator;
  type: 'group';
  campaign?: { proposal_id: string; name: string };
  webForm?: { leads_form_id: number | string; leads_form_name: string };
  conditions: ConditionRule[];
  subGroups: LogicalGroup[];
}

interface QueryParameterProps {
  group: LogicalGroup;
  onGroupChange: (updatedGroup: LogicalGroup) => void;
  groupLabel?: string;
  groupColor?: 'primary' | 'secondary' | 'default';
  canDelete?: boolean;
  onDelete?: () => void;
  nestLevel?: number;
  parentResults?: Array<{ label: string; value: string }>;
  currentEntityFormFields?: any[];
  entityPreviewData?: any[];
  handleSelectParentResult?: (selected: any | null) => void;
}

// Constants
// const operatorLabel: Record<Operator, string> = {
//     equals: "Equals",
//     not_equals: "Not equals",
//     contains: "Contains",
//     starts_with: "Starts with",
//     ends_with: "Ends with",
//     gt: "Greater",
//     gte: "Greate or Equal",
//     lt: "Less",
//     lte: "Less or Equal",
//     is_empty: "Is empty",
//     is_not_empty: "Is not empty",
// };

// const stringOps: Operator[] = ["equals", "not_equals", "contains", "starts_with", "ends_with", "gt", "gte", "lt", "lte"];
// const numericOps: Operator[] = ["equals", "not_equals", "gt", "gte", "lt", "lte"];

// const getOperatorOptions = (valueType: ValueType) => {
//     const valueOps = valueType === "number" ? numericOps : stringOps;
//     const availableOps: Operator[] = [...valueOps, "is_empty", "is_not_empty"];
//     return availableOps.map((op) => ({ label: operatorLabel[op], value: op }));
// };

// const valueTypeOptions = [
//     { label: "String", value: "string" },
//     { label: "Number", value: "number" },
//     { label: "Boolean", value: "boolean" },
//     { label: "Date (ISO)", value: "date" },
// ];

const defaultRule = (suffix: string | number): ConditionRule => ({
  id: `rule-${suffix}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
  left: '',
  operator: 'equals',
  valueType: 'string',
  caseInsensitive: true,
  trim: true,
});

const defaultGroup = (
  suffix: string | number,
  operator: LogicalOperator = 'AND'
): LogicalGroup => ({
  id: `${suffix}`,
  operator,
  type: 'group',
  conditions: [defaultRule(suffix)],
  subGroups: [],
});

const EntityQueryParameters: React.FC<QueryParameterProps> = ({
  group,
  onGroupChange,
  groupLabel = 'Query Parameters',
  groupColor = 'primary',
  canDelete = false,
  onDelete,
  nestLevel = 0,
  parentResults = [],
  currentEntityFormFields = [],
  entityPreviewData = {},
  handleSelectParentResult = () => {},
}) => {
  const conditions = group.conditions || [];
  const subGroups = group.subGroups || [];

  // Update condition
  const updateCondition = (index, patch) => {
    const updatedConditions = [...conditions];
    updatedConditions[index] = { ...updatedConditions[index], ...patch };
    onGroupChange({ ...group, conditions: updatedConditions });
  };

  const removeCondition = (index: number) => {
    const updatedConditions = [...conditions];
    updatedConditions.splice(index, 1);
    onGroupChange({ ...group, conditions: updatedConditions });
  };

  const addCondition = () => {
    onGroupChange({
      ...group,
      conditions: [...conditions, defaultRule(group.id)],
    });
  };

  const addSubGroup = () => {
    onGroupChange({
      ...group,
      subGroups: [
        ...subGroups,
        defaultGroup(
          `${group.id}_${subGroups.length + 1}`,
          group.operator === 'AND' ? 'OR' : 'AND'
        ),
      ],
    });
  };

  const updateSubGroup = (index: number, updatedSubGroup: LogicalGroup) => {
    const updatedSubGroups = [...subGroups];
    updatedSubGroups[index] = updatedSubGroup;
    onGroupChange({ ...group, subGroups: updatedSubGroups });
  };

  const removeSubGroup = (index: number) => {
    const updatedSubGroups = [...subGroups];
    updatedSubGroups.splice(index, 1);
    onGroupChange({ ...group, subGroups: updatedSubGroups });
  };

  const modifyEntityFormFieldsArray = (list: any[]) => {
    if (!list) return [];
    try {
      const currentEntityFormFields =
        list
          ?.filter((item) => !['file', 'video', 'image', 'audio', 'document']?.includes(item?.type)) // keep only valid ones
          .map((item) => ({ name: item?.name, label: item?.label })) ?? [];
      return currentEntityFormFields;
    } catch (error) {
      console.log(error);
      return [];
    }
  };
  // UI for a single condition
  const renderConditionRule = (rule: ConditionRule, index: number) => {
    const numericOperators: Operator[] = ['gt', 'gte', 'lt', 'lte'];

    const fieldsToShow = numericOperators.includes(rule?.operator as Operator)
      ? currentEntityFormFields?.filter((f) => ['int', 'float']?.includes(f?.type))
      : currentEntityFormFields;
    return (
      <>
        {index > 0 && (
          <Stack direction="row" alignItems="center" sx={{ my: 1 }}>
            <Divider sx={{ flex: 1, borderColor: 'rgb(122 122 122 / 57%)' }} />
            <CommonBadges
              size="small"
              value={group?.operator}
              color={group?.operator === 'AND' ? 'primary' : 'warning'}
            />
            <Divider sx={{ flex: 1, borderColor: 'rgb(122 122 122 / 57%)' }} />
          </Stack>
        )}

        <Box key={rule.id} sx={{ mb: 2, p: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <SearchableDropdown
                  sx={{ minWidth: 120 }}
                  options={[...parentResults, { label: 'Others', value: 'others' }]}
                  value={
                    [...parentResults, { label: 'Others', value: 'others' }]?.find(
                      (item) => item?.value === rule?.left
                    ) ?? null
                  }
                  onChange={(e) => {
                    if (e?.node_id) {
                      handleSelectParentResult(e);
                    }
                    updateCondition(index, {
                      left: e?.value ?? '',
                      ...(e?.node_id && { node_id: e?.node_id ?? '' }),
                    });
                  }}
                  label={'Select Parameter'}
                />
              </Grid>
              {rule?.node_id &&
                modifyEntityFormFieldsArray(entityPreviewData?.[rule?.node_id]?.form_fields ?? [])
                  ?.length > 0 && (
                  <Grid item xs={12} md={3}>
                    <SearchableDropdown
                      options={
                        modifyEntityFormFieldsArray(
                          entityPreviewData?.[rule?.node_id]?.form_fields
                        ) ?? []
                      }
                      value={
                        modifyEntityFormFieldsArray(
                          entityPreviewData?.[rule?.node_id]?.form_fields
                        )?.find((item) => item?.name === rule?.previous_entity_form_field?.name) ??
                        null
                      }
                      onChange={(e) => {
                        updateCondition(index, {
                          previous_entity_form_field: { name: e?.name ?? '' },
                        });
                      }}
                      label={
                        <Typography>
                          Select Previous Node Entity Form Field{' '}
                          <span style={{ color: 'red' }}>*</span>
                        </Typography>
                      }
                    />
                  </Grid>
                )}
              <Grid item xs={12} md={3}>
                <SearchableDropdown
                  sx={{ minWidth: 120 }}
                  options={getOperatorOptions(rule?.valueType) ?? []}
                  value={
                    (getOperatorOptions(rule?.valueType) ?? [])?.find(
                      (item) => item?.value === rule?.operator
                    ) ?? null
                  }
                  onChange={(e) =>
                    updateCondition(index, {
                      operator: e?.value ?? '',
                    })
                  }
                  label={'Select Operator'}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <SearchableDropdown
                  // options={currentEntityFormFields}
                  options={fieldsToShow}
                  value={
                    fieldsToShow?.find((item) => item?.name === rule?.matched_with?.name) ?? null
                  }
                  // value={currentEntityFormFields?.find((item) => item?.name === rule?.matched_with?.name) ?? null}
                  valueKey="name"
                  onChange={(e) => {
                    updateCondition(index, { matched_with: { name: e?.name } });
                  }}
                  label={
                    <Typography>
                      Select Form Field <span style={{ color: 'red' }}>*</span>
                    </Typography>
                  }
                />
              </Grid>
              {rule?.left === 'others' && (
                <Grid item xs={12} md={3}>
                  <TextField
                    size="small"
                    label="Enter Value"
                    value={rule?.custom_value ?? ''}
                    onChange={(e) =>
                      updateCondition(index, { custom_value: e?.target?.value ?? '' })
                    }
                  />
                </Grid>
              )}
            </Grid>
            {conditions?.length > 1 && (
              <Box display={'flex'} justifyContent={'flex-end'} aria-label="Remove condition">
                <IconButton size="small" onClick={() => removeCondition(index)}>
                  <DeleteOutlineIcon fontSize="small" color="error" />
                </IconButton>
              </Box>
            )}
          </Stack>
        </Box>
      </>
    );
  };

  return (
    <Box
      className="bot-builder"
      sx={{
        ml: nestLevel * 2,
        mb: 2,
        mt: 2,
        p: 2,
        borderLeft: nestLevel > 0 ? '3px solid #e0e0e0' : undefined,
        bgcolor: nestLevel === 0 ? 'rgba(25, 118, 210, 0.04)' : 'rgba(0,0,0,0.02)',
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="subtitle2">
          {nestLevel === 0 ? groupLabel : `Group (${group.operator})`}
        </Typography>
        <FormControl size="small" sx={{ minWidth: 80 }}>
          <Select
            value={group?.operator ?? 'AND'}
            onChange={(e) =>
              onGroupChange({ ...group, operator: e.target.value as LogicalOperator })
            }
          >
            <MenuItem value="AND">AND</MenuItem>
            <MenuItem value="OR">OR</MenuItem>
          </Select>
        </FormControl>
        {canDelete && (
          <IconButton size="small" onClick={onDelete}>
            <DeleteOutlineIcon fontSize="small" color="error" />
          </IconButton>
        )}
      </Stack>
      <Box>
        <Card sx={{ mb: 2 }}>{conditions?.map(renderConditionRule)}</Card>

        <Button
          size="small"
          variant="outlined"
          startIcon={<AddCircleOutline />}
          onClick={addCondition}
          sx={{ mr: 1 }}
        >
          Add Condition
        </Button>
        <Button
          size="small"
          variant="outlined"
          color="secondary"
          startIcon={<AddCircleOutline />}
          onClick={addSubGroup}
        >
          Add Sub-group
        </Button>
      </Box>
      {(subGroups ?? [])?.map((sub, idx) => (
        <EntityQueryParameters
          key={sub.id}
          group={sub}
          onGroupChange={(updatedSub) => updateSubGroup(idx, updatedSub)}
          groupLabel={groupLabel}
          groupColor={groupColor}
          canDelete={true}
          onDelete={() => removeSubGroup(idx)}
          nestLevel={nestLevel + 1}
          parentResults={parentResults}
          currentEntityFormFields={currentEntityFormFields}
        />
      ))}
    </Box>
  );
};

export default EntityQueryParameters;
